import React, { useState } from 'react';
import { bookings } from '../../api';
import './Bookings.css';

const LESSON_DURATIONS = [
  { value: 30, label: '30 minutes' },
  { value: 60, label: '60 minutes (1 hour)' },
  { value: 90, label: '90 minutes' },
  { value: 120, label: '120 minutes (2 hours)' }
];

const SESSION_TYPES = [
  { value: 'custom', label: 'Custom' },
  { value: 'technique', label: '🎾 Technique Training' },
  { value: 'match-play', label: '🏆 Match Play' },
  { value: 'drill-practice', label: '🎯 Drill Practice' },
  { value: 'match-prep', label: '📋 Match Prep' },
  { value: 'mental', label: '🧠 Mental Training' }
];

const COMMON_TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Europe/London',
  'Europe/Paris',
  'Asia/Tokyo',
  'Australia/Sydney'
];

export default function CreateRecurringModal({ isOpen, onClose, onSuccess }) {
  const [step, setStep] = useState(1); // 1: Basic, 2: Schedule, 3: Time, 4: Review
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    sessionType: 'custom',
    tags: '',
    maxBookings: 1,
    // Schedule
    frequency: 'weekly', // 'daily', 'weekly', 'biweekly'
    daysOfWeek: [], // [0=Sun, 1=Mon, ..., 6=Sat]
    until: '', // Date string (YYYY-MM-DD)
    // Time
    timeOfDay: '14:00',
    durationMinutes: 60,
    timezone: 'America/New_York'
  });

  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [preview, setPreview] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDayToggle = (day) => {
    setFormData(prev => ({
      ...prev,
      daysOfWeek: prev.daysOfWeek.includes(day)
        ? prev.daysOfWeek.filter(d => d !== day)
        : [...prev.daysOfWeek, day].sort((a, b) => a - b)
    }));
  };

  const generateRRULE = () => {
    try {
      const dayMap = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];
      const byDays = formData.daysOfWeek.map(d => dayMap[d]).join(',');

      let rrule = `FREQ=${formData.frequency.toUpperCase()}`;

      if (formData.frequency === 'weekly' && byDays) {
        rrule += `;BYDAY=${byDays}`;
      }

      if (formData.until) {
        // Convert date string to RRULE format (YYYYMMDD)
        const [year, month, day] = formData.until.split('-');
        rrule += `;UNTIL=${year}${month}${day}`;
      }

      return rrule;
    } catch (err) {
      console.error('Error generating RRULE:', err);
      return null;
    }
  };

  const generatePreview = () => {
    try {
      const rrule = generateRRULE();
      if (!rrule) {
        setPreview('⚠️ Invalid schedule configuration');
        return;
      }

      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const selectedDays = formData.daysOfWeek.map(d => dayNames[d]).join(', ');

      const freqLabel = {
        'daily': 'every day',
        'weekly': 'every week',
        'biweekly': 'every two weeks'
      }[formData.frequency];

      let previewText = `📅 Every week on ${selectedDays}`;
      previewText += `\n⏰ Time: ${formData.timeOfDay} (${formData.timezone})`;
      previewText += `\n⏱️ Duration: ${formData.durationMinutes} minutes`;

      if (formData.until) {
        const untilDate = new Date(formData.until);
        previewText += `\n📍 Until: ${untilDate.toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })}`;
      }

      setPreview(previewText);
    } catch (err) {
      console.error('Error generating preview:', err);
      setPreview('⚠️ Error generating preview');
    }
  };

  const handleNext = () => {
    setError(null);

    if (step === 1) {
      if (!formData.title.trim()) {
        setError('Pattern title is required');
        return;
      }
      setStep(2);
    } else if (step === 2) {
      if (formData.daysOfWeek.length === 0) {
        setError('Select at least one day of the week');
        return;
      }
      if (!formData.until) {
        setError('End date is required');
        return;
      }
      generatePreview();
      setStep(3);
    } else if (step === 3) {
      setStep(4);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
      setError(null);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      const rrule = generateRRULE();
      if (!rrule) {
        throw new Error('Invalid recurrence rule');
      }

      const tags = formData.tags
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);

      const patternData = {
        title: formData.title.trim(),
        description: formData.description.trim() || null,
        sessionType: formData.sessionType,
        tags,
        maxBookings: Math.min(parseInt(formData.maxBookings) || 1, 4),
        rrule,
        timeOfDay: formData.timeOfDay,
        durationMinutes: parseInt(formData.durationMinutes),
        timezone: formData.timezone
      };

      await bookings.createRecurringPattern(patternData);

      setFormData({
        title: '',
        description: '',
        sessionType: 'custom',
        tags: '',
        maxBookings: 1,
        frequency: 'weekly',
        daysOfWeek: [],
        until: '',
        timeOfDay: '14:00',
        durationMinutes: 60,
        timezone: 'America/New_York'
      });
      setStep(1);

      if (onSuccess) {
        setTimeout(() => onSuccess(), 500);
      }
      setTimeout(() => onClose(), 1000);
    } catch (err) {
      console.error('❌ Error creating recurring pattern:', err);
      setError(err.response?.data?.message || 'Failed to create recurring pattern');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="booking-modal-overlay" onClick={onClose}>
      <div className="booking-modal-content" onClick={e => e.stopPropagation()}>
        <div className="booking-modal-header">
          <h2>📅 Create Recurring Coaching Pattern (Step {step}/4)</h2>
          <button className="booking-modal-close" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit} className="booking-form">
          {error && <div className="booking-error">{error}</div>}

          {/* STEP 1: BASIC INFO */}
          {step === 1 && (
            <>
              <div className="booking-form-group">
                <label htmlFor="title">Pattern Title *</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="e.g., Monday Forehand Training"
                  maxLength="100"
                  required
                />
              </div>

              <div className="booking-form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Optional: What's the focus of these sessions?"
                  maxLength="500"
                  rows="3"
                />
              </div>

              <div className="booking-form-group">
                <label htmlFor="sessionType">Session Type</label>
                <select
                  id="sessionType"
                  name="sessionType"
                  value={formData.sessionType}
                  onChange={handleInputChange}
                >
                  {SESSION_TYPES.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="booking-form-group">
                <label htmlFor="maxBookings">Max Participants (1-4)</label>
                <select
                  id="maxBookings"
                  name="maxBookings"
                  value={formData.maxBookings}
                  onChange={handleInputChange}
                >
                  <option value="1">1 person</option>
                  <option value="2">2 people</option>
                  <option value="3">3 people</option>
                  <option value="4">4 people</option>
                </select>
              </div>

              <div className="booking-form-group">
                <label htmlFor="tags">Tags (comma-separated, optional)</label>
                <input
                  type="text"
                  id="tags"
                  name="tags"
                  value={formData.tags}
                  onChange={handleInputChange}
                  placeholder="e.g., forehand, serve, intermediate"
                  maxLength="100"
                />
              </div>
            </>
          )}

          {/* STEP 2: SCHEDULE */}
          {step === 2 && (
            <>
              <div className="booking-form-group">
                <label>Days of Week (select at least one) *</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', marginBottom: '16px' }}>
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => handleDayToggle(idx)}
                      style={{
                        padding: '12px',
                        borderRadius: '6px',
                        border: formData.daysOfWeek.includes(idx) ? '2px solid #3498db' : '1px solid #bdc3c7',
                        background: formData.daysOfWeek.includes(idx) ? '#e8f4f8' : '#fff',
                        cursor: 'pointer',
                        fontWeight: formData.daysOfWeek.includes(idx) ? 'bold' : 'normal',
                        color: formData.daysOfWeek.includes(idx) ? '#3498db' : '#2c3e50'
                      }}
                    >
                      {day}
                    </button>
                  ))}
                </div>
              </div>

              <div className="booking-form-group">
                <label htmlFor="frequency">Frequency *</label>
                <select
                  id="frequency"
                  name="frequency"
                  value={formData.frequency}
                  onChange={handleInputChange}
                >
                  <option value="weekly">Every week</option>
                  <option value="biweekly">Every 2 weeks</option>
                </select>
              </div>

              <div className="booking-form-group">
                <label htmlFor="until">End Date (pattern expires after this) *</label>
                <input
                  type="date"
                  id="until"
                  name="until"
                  value={formData.until}
                  onChange={handleInputChange}
                  min={new Date().toISOString().split('T')[0]}
                  required
                />
              </div>
            </>
          )}

          {/* STEP 3: TIME SETTINGS */}
          {step === 3 && (
            <>
              <div className="booking-form-group">
                <label htmlFor="timeOfDay">Start Time *</label>
                <input
                  type="time"
                  id="timeOfDay"
                  name="timeOfDay"
                  value={formData.timeOfDay}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="booking-form-group">
                <label htmlFor="durationMinutes">Duration *</label>
                <select
                  id="durationMinutes"
                  name="durationMinutes"
                  value={formData.durationMinutes}
                  onChange={handleInputChange}
                  required
                >
                  {LESSON_DURATIONS.map(dur => (
                    <option key={dur.value} value={dur.value}>
                      {dur.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="booking-form-group">
                <label htmlFor="timezone">Timezone *</label>
                <select
                  id="timezone"
                  name="timezone"
                  value={formData.timezone}
                  onChange={handleInputChange}
                  required
                >
                  {COMMON_TIMEZONES.map(tz => (
                    <option key={tz} value={tz}>{tz}</option>
                  ))}
                </select>
              </div>
            </>
          )}

          {/* STEP 4: REVIEW */}
          {step === 4 && (
            <div style={{
              padding: '16px',
              background: '#f0f7ff',
              borderRadius: '8px',
              borderLeft: '4px solid #3498db',
              whiteSpace: 'pre-line',
              fontSize: '14px',
              color: '#2c3e50',
              marginBottom: '16px',
              fontFamily: 'monospace'
            }}>
              {preview}
            </div>
          )}

          <div className="booking-form-actions">
            {step > 1 && (
              <button type="button" className="booking-btn-secondary" onClick={handleBack}>
                ← Back
              </button>
            )}
            {step < 4 && (
              <button type="button" className="booking-btn-primary" onClick={handleNext}>
                Next →
              </button>
            )}
            {step === 4 && (
              <>
                <button type="button" className="booking-btn-secondary" onClick={onClose}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="booking-btn-primary"
                  disabled={submitting}
                >
                  {submitting ? '⏳ Creating...' : '✅ Create Pattern'}
                </button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
